import { Check } from "lucide-react";

/**
 * Visual progress strip used on the Register "check your email" screen
 * and the Dashboard pending banner. Reassures applicants and reduces
 * "did my application go through?" support tickets.
 *
 * Stages: "submitted" | "verified" | "approved"
 * - submitted: just registered, email not yet verified
 * - verified: email confirmed, awaiting admin review
 * - approved: full access (shown briefly or for context)
 */
const STEPS = [
  { key: "submitted", label: "Application received", hint: "Just now" },
  { key: "verified", label: "Confirm your email", hint: "Check your inbox" },
  { key: "review", label: "Editorial review", hint: "Usually within 24 hours" },
  { key: "welcome", label: "Welcome to AfricaArtMasters", hint: "Start collecting or selling" },
];

const stageIndex = (stage) => {
  switch (stage) {
    case "submitted":
      return 0;
    case "verified":
      return 1;
    case "approved":
      return 3;
    default:
      return 0;
  }
};

export const ApplyTimeline = ({ stage = "submitted" }) => {
  // The "active" step is the one the user is currently on or waiting at.
  // Stages map to which step is highlighted (1-based index of progress).
  let activeIndex;
  if (stage === "submitted") activeIndex = 1;        // waiting on email verification
  else if (stage === "verified") activeIndex = 2;    // waiting on editorial review
  else activeIndex = 3;                              // approved → all done

  return (
    <ol data-testid="apply-timeline" className="mt-10 border border-[#E5E0D8] bg-white">
      {STEPS.map((step, i) => {
        const done = i < activeIndex;
        const active = i === activeIndex;
        return (
          <li
            key={step.key}
            data-testid={`timeline-step-${i}`}
            data-state={done ? "done" : active ? "active" : "pending"}
            className={`flex items-start gap-4 px-5 py-4 border-b border-[#E5E0D8] last:border-b-0 ${
              active ? "bg-[#F4EFE6]/50" : ""
            }`}
          >
            <span
              className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-mono-aam text-xs ${
                done
                  ? "bg-[#1E3A8A] text-[#F4C430]"
                  : active
                  ? "bg-[#F4C430] text-[#1E3A8A]"
                  : "border border-[#E5E0D8] text-[#555]"
              }`}
              aria-hidden="true"
            >
              {done ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p
                className={`font-serif-display text-base ${
                  done || active ? "text-[#111]" : "text-[#555]"
                }`}
              >
                {step.label}
              </p>
              <p className="label-eyebrow text-[#555] mt-0.5">
                {done && i === stageIndex(stage) - 1 ? "Done" : step.hint}
              </p>
            </div>
            {active && (
              <span className="label-eyebrow text-[#1E3A8A] flex-shrink-0">In progress</span>
            )}
          </li>
        );
      })}
    </ol>
  );
};
